import { ChangeDetectionStrategy, Component, inject, OnInit, AfterViewInit, ChangeDetectorRef, DestroyRef } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogModule,
  MatDialogRef
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { EditorRole, getEditorRoleViewValue } from '../editor.role.enum';
import { generateSecurePassword } from '../../core/util';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatDividerModule } from '@angular/material/divider';
import { SubjectJsonModel } from '../../subject/subject.json.model';
import { AsyncPipe } from '@angular/common';
import { select, Store } from '@ngxs/store';
import { AppSelectors } from '../../app.state';
import { PasswordStrengthComponent } from '../password-strength/password-strength.component';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { firstValueFrom, from, Observable, switchMap } from 'rxjs';
import { ConsumerEntity } from '../../consumer/consumer.entity';
import { ConsumerEntityService } from '../../consumer/services/consumer.entity.service';
import { normalizeManyRelations } from '../../core/decorators/relation.decorator';
import { WidgetMessageService } from '../../core/services/widget-message.service';
import { SubjectEntityService } from '../../subject/services/subject.entity.service';
import { SubjectEntity } from '../../subject/subject.entity';
import { EditorEntity } from '../editor.entity';
import { EditorEntityService } from '../services/editor.entity.service';

@Component({
  selector: 'pixl-editor-edit',
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatDialogModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatButtonToggleModule,
    MatDividerModule,
    AsyncPipe,
    PasswordStrengthComponent,
    TranslocoModule
  ],
  templateUrl: './editor-edit.component.html',
  styleUrl: './editor-edit.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class EditorEditComponent implements OnInit, AfterViewInit {
  dialogRef = inject(MatDialogRef<EditorEditComponent, EditorEntity>);
  data? = inject<Partial<EditorEntity>>(MAT_DIALOG_DATA);

  editors$!: Observable<EditorEntity[]>;
  subjects$!: Observable<SubjectEntity[]>;
  consumers$!: Observable<ConsumerEntity[]>;
  currentPage = 1;
  pageSize = 20;

  private readonly store = inject(Store);
  private translocoService = inject(TranslocoService);
  private consumerService = inject(ConsumerEntityService);
  private subjectService = inject(SubjectEntityService);
  private editorService = inject(EditorEntityService);
  private widgetService = inject(WidgetMessageService);
  private destroyRef = inject(DestroyRef);
  public cdr = inject(ChangeDetectorRef);
  private dialog = inject(MatDialog);
  private fb = inject(FormBuilder);

  public formGroup: FormGroup;

  initialRole = this.data?.role;
  strongPassword = false;

  public authState = select(AppSelectors.authState);

  public dataSubjects: SubjectJsonModel[] = [];

  roles: EditorRole[] = Object.values(EditorRole) as EditorRole[];
  showPassword = false;

  isEditMode = !!this.data?.email;

  constructor() {
    this.formGroup = this.fb.group({
      email: [this.data?.email, [Validators.required, Validators.email]],
      password: [this.data?.password || '', this.isEditMode ? [] : [Validators.required]],
      role: [this.data?.role, [Validators.required]],
      subjects: [this.data?.subjects || []],
      consumerIds: [this.data?.consumerIds || []],
      version: [this.data?.version || 1],
      consumers: [normalizeManyRelations(this.data?.consumers)],
      subjectsRel: [normalizeManyRelations(this.data?.subjectsRel)],
    });
  }

  public ngOnInit(): void {
    this.formGroup.markAllAsTouched();
    this.loadRelationData();
  }

  private loadRelationData(): void {
    const consumerQuery = { sort: 'name' };
    this.consumers$ = from(
      this.consumerService.ensureLoaded(consumerQuery)
    ).pipe(
      switchMap(() =>
        this.store.select(this.consumerService.allByQuery(consumerQuery))
      )
    );

    const subjectQuery = { sort: 'name' };
    this.subjects$ = from(
      this.subjectService.ensureLoaded(subjectQuery)
    ).pipe(
      switchMap(() =>
        this.store.select(this.subjectService.allByQuery(subjectQuery))
      )
    );
  }

  public ngAfterViewInit(): void {
    this.formGroup.markAllAsTouched();
  }

  protected isOwnAccount(): boolean {
    return this.authState().editor?.email === this.formGroup.get('email')?.value;
  }

  public async emailExists(): Promise<boolean> {
    const email = this.formGroup.get('email')!.value;

    const query: Record<string, string | number> = {
      'include': 'consumers,subjectsRel',
      'page[size]': 10,
      'page[number]': 1,
      'sort': 'id',
      'filter[idOrEmailLike]': email
    };

    await this.editorService.ensureLoaded(query);

    const editors = await firstValueFrom(
      this.store.select(this.editorService.allByQuery(query))
    );

    if (editors.length > 0) {
      return true;
    }
    return false;
  }

  async onSave(): Promise<void> {
    const subjectIds = this.formGroup.value.subjectsRel as string[];
    const consumerIds = this.formGroup.value.consumers as string[];
    const formValue = this.formGroup.value;

    const updatedEditor = Object.assign(
      new EditorEntity(),
      { id: this.data?.id },
      formValue,
      {
        subjects: subjectIds,
        consumerIds: consumerIds,
        consumers: consumerIds?.map(id => ({
          type: 'consumers',
          id,
        })),

        subjectsRel: subjectIds?.map(id => ({
          type: 'subjects',
          id,
        })),
      },
    );

    if (await this.emailExists() && !this.isEditMode) {
      this.widgetService.setMessage({
        message: this.translocoService.translate('email_exists'),
        messageTye: 'mtError',
      });
      return;
    }

    if (!this.formGroup.valid) {
      this.widgetService.setMessage({
        message: this.translocoService.translate('required_error'),
        messageTye: 'mtError',
      });
      return;
    }

    if (this.isOwnAccount() && this.initialRole != this.formGroup.get('role')?.value) {
      this.widgetService.setMessage({
        message: this.translocoService.translate('cant_change_own_role'),
        messageTye: 'mtError',
      });
      return;
    }

    this.dialogRef.close(updatedEditor);
  }

  onPasswordStrengthChanged(event: boolean) {
    this.strongPassword = event;
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  generatePassword(): void {
    this.formGroup.patchValue({
      password: generateSecurePassword()
    });
  }

  public getEditorRole(role: EditorRole): string {
    return this.translocoService.translate(role);
  }

  protected readonly getEditorRoleViewValue = getEditorRoleViewValue;
}
